// Serviço para comunicação OPC UA com múltiplos servidores
export interface OPCUAData {
  activePower: number;
  windSpeed: number;
  rotorRPM: number;
  generatorTemp: number;
  gearboxTemp: number;
  vibration: number;
  operationalStatus: number;
  bladeAngle: number;
  energyAccumulated: number;
  availability: number;
  lastUpdate: Date;
}

export interface TurbineServer {
  id: string;
  name: string;
  model: string;
  url: string;
  port: number;
  requiresAuth: boolean;
  username?: string;
  password?: string;
  maxPower: number;
  path: string;
  complex: string;
  park: string;
}

export interface TurbineConnection {
  server: TurbineServer;
  isConnected: boolean;
  lastData: OPCUAData | null;
  connectionAttempts: number;
  authenticated: boolean;
}

class MultiOPCUAService {
  // Lista de servidores configurados com persistência local
  private turbineServers: TurbineServer[] = [];

  private connections: Map<string, TurbineConnection> = new Map();
  private maxConnectionAttempts = 3;

  constructor() {
    this.initializeDefaultTurbines();
    this.initializeConnections();
  }

  private initializeDefaultTurbines() {
    // Carregar turbinas do localStorage ou usar padrões
    const savedTurbines = localStorage.getItem('turbineServers');
    if (savedTurbines) {
      try {
        const parsed = JSON.parse(savedTurbines);
        // Sempre reconfigurar para garantir que temos as 3 turbinas
        console.log('🔄 Reconfigurando sistema para garantir 3 turbinas operacionais...');
        this.setupDefaultTurbines();
      } catch (error) {
        console.warn('⚠️ Erro ao carregar turbinas salvas, usando configuração padrão');
        this.setupDefaultTurbines();
      }
    } else {
      this.setupDefaultTurbines();
    }
  }

  // Função para gerar configuração de 149 aerogeradores em 13 parques
  private generateTurbineServers(): TurbineServer[] {
    const servers: TurbineServer[] = [];
    
    // Definição dos 13 parques com distribuição realista
    const parkDefinitions = [
      { complex: 'CE-PW-001', park: 'PW-001', name: 'Parque Ventos do Nordeste', turbines: 15, model: 'WEG AGW110/2.1', power: 2.1 },
      { complex: 'CE-PW-001', park: 'PW-002', name: 'Parque Caatinga', turbines: 12, model: 'WEG AGW147/4.2', power: 4.2 },
      { complex: 'CE-PW-001', park: 'PW-003', name: 'Parque Serra Branca', turbines: 14, model: 'WEG AGW172/7.5', power: 7.5 },
      
      { complex: 'CE-PW-002', park: 'PW-004', name: 'Parque Altiplano', turbines: 11, model: 'WEG AGW110/2.1', power: 2.1 },
      { complex: 'CE-PW-002', park: 'PW-005', name: 'Parque Chapada', turbines: 13, model: 'WEG AGW147/4.2', power: 4.2 },
      { complex: 'CE-PW-002', park: 'PW-006', name: 'Parque Vale dos Ventos', turbines: 10, model: 'WEG AGW172/7.5', power: 7.5 },
      
      { complex: 'CE-PW-003', park: 'PW-007', name: 'Parque Agreste', turbines: 12, model: 'WEG AGW110/2.1', power: 2.1 },
      { complex: 'CE-PW-003', park: 'PW-008', name: 'Parque Sertão Central', turbines: 11, model: 'WEG AGW147/4.2', power: 4.2 },
      { complex: 'CE-PW-003', park: 'PW-009', name: 'Parque Planalto', turbines: 9, model: 'WEG AGW172/7.5', power: 7.5 },
      
      { complex: 'CE-PW-004', park: 'PW-010', name: 'Parque Litoral Sul', turbines: 13, model: 'WEG AGW110/2.1', power: 2.1 },
      { complex: 'CE-PW-004', park: 'PW-011', name: 'Parque Mata Atlântica', turbines: 8, model: 'WEG AGW147/4.2', power: 4.2 },
      
      { complex: 'CE-PW-005', park: 'PW-012', name: 'Parque Cerrado Norte', turbines: 10, model: 'WEG AGW172/7.5', power: 7.5 },
      { complex: 'CE-PW-005', park: 'PW-013', name: 'Parque Grande Sertão', turbines: 11, model: 'WEG AGW110/2.1', power: 2.1 }
    ];

    let turbineCounter = 1;

    parkDefinitions.forEach(parkDef => {
      for (let i = 1; i <= parkDef.turbines; i++) {
        const turbineId = `${parkDef.park}-T-${String(i).padStart(3, '0')}`;
        const turbineNum = String(turbineCounter).padStart(3, '0');
        
        // Simular diferentes configurações de conexão
        const portVariation = (turbineCounter % 3);
        const basePort = 4334;
        const port = basePort + portVariation;
        const requiresAuth = (turbineCounter % 4 === 0); // 25% das turbinas com autenticação
        
        servers.push({
          id: turbineId,
          name: `Prime Wind Turbine ${turbineNum}`,
          model: parkDef.model,
          url: 'opc.tcp://localhost',
          port: port,
          path: `/PrimeWindTurbine${turbineNum}`,
          requiresAuth: requiresAuth,
          username: requiresAuth ? 'SCADA' : undefined,
          password: requiresAuth ? 'SCADA' : undefined,
          maxPower: parkDef.power,
          complex: parkDef.complex,
          park: parkDef.park
        });
        
        turbineCounter++;
      }
    });

    return servers;
  }

  private setupDefaultTurbines() {
    // Gerar todas as 149 turbinas
    this.turbineServers = this.generateTurbineServers();
    
    console.log(`🌪️ Sistema expandido: ${this.turbineServers.length} aerogeradores configurados`);
    console.log(`📊 Distribuição por complexo:`);
    
    // Mostrar estatísticas de distribuição
    const complexStats: { [key: string]: { parks: Set<string>, turbines: number } } = {};
    this.turbineServers.forEach(server => {
      if (!complexStats[server.complex]) {
        complexStats[server.complex] = { parks: new Set(), turbines: 0 };
      }
      complexStats[server.complex].parks.add(server.park);
      complexStats[server.complex].turbines++;
    });
    
    Object.entries(complexStats).forEach(([complex, stats]) => {
      console.log(`   - ${complex}: ${stats.turbines} turbinas em ${stats.parks.size} parques`);
    });
    
    this.saveTurbinesToStorage();
  }

  private saveTurbinesToStorage() {
    try {
      localStorage.setItem('turbineServers', JSON.stringify(this.turbineServers));
      console.log(`💾 Configurações de ${this.turbineServers.length} turbinas salvas no armazenamento local`);
    } catch (error) {
      console.error('❌ Erro ao salvar configurações de turbinas:', error);
    }
  }

  private async initializeConnections() {
    for (const server of this.turbineServers) {
      const connection: TurbineConnection = {
        server,
        isConnected: false,
        lastData: null,
        connectionAttempts: 0,
        authenticated: false
      };
      
      this.connections.set(server.id, connection);
      await this.connectToServer(server.id);
    }
  }

  // Detectar se o servidor requer autenticação
  private async detectAuthenticationRequirement(url: string): Promise<boolean> {
    try {
      console.log(`🔍 Testando conexão OPC UA: ${url}`);
      // Simular tentativa de conexão sem autenticação
      // Em implementação real, seria feita conexão OPC UA
      
      // Para demonstração: localhost:4334 = sem auth, outros = com auth
      if (url.includes('4334')) {
        console.log(`🔓 Servidor não requer autenticação`);
        return false;
      } else {
        console.log(`🔐 Servidor requer autenticação`);
        return true;
      }
    } catch (error) {
      console.log(`🔐 Assumindo que servidor requer autenticação devido ao erro`);
      return true;
    }
  }

  private async connectToServer(turbineId: string, retryWithAuth: boolean = false) {
    const connection = this.connections.get(turbineId);
    if (!connection) return;

    try {
      const fullUrl = `${connection.server.url}:${connection.server.port}${connection.server.path}`;
      console.log(`🔗 Conectando ao servidor OPC UA [${turbineId}]:`, fullUrl);
      
      // Se não sabemos se precisa auth, detectar automaticamente
      if (!retryWithAuth && !connection.server.requiresAuth) {
        const needsAuth = await this.detectAuthenticationRequirement(fullUrl);
        if (needsAuth && !connection.server.username) {
          console.log(`⚠️ Servidor [${turbineId}] requer autenticação mas credenciais não fornecidas`);
          connection.isConnected = false;
          return;
        }
        connection.server.requiresAuth = needsAuth;
      }
      
      if (connection.server.requiresAuth) {
        console.log(`🔐 Servidor requer autenticação: ${connection.server.username}/***`);
        // Simular autenticação
        if (connection.server.username && connection.server.password) {
          connection.authenticated = true;
          console.log(`✅ Autenticação bem-sucedida para ${turbineId}`);
        } else {
          throw new Error('Credenciais obrigatórias não fornecidas');
        }
      } else {
        connection.authenticated = true;
      }
      
      connection.isConnected = true;
      connection.connectionAttempts = 0;
      console.log(`✅ Conectado ao servidor OPC UA [${turbineId}] com sucesso`);
      
    } catch (error) {
      console.error(`❌ Erro ao conectar ao servidor OPC UA [${turbineId}]:`, error);
      connection.isConnected = false;
      connection.authenticated = false;
      connection.connectionAttempts++;
      
      if (connection.connectionAttempts < this.maxConnectionAttempts) {
        setTimeout(() => this.connectToServer(turbineId, true), 5000);
      }
    }
  }

  // Ler dados de uma turbina específica
  async readTurbineData(turbineId: string): Promise<OPCUAData | null> {
    const connection = this.connections.get(turbineId);
    if (!connection) {
      console.warn(`⚠️ Turbina ${turbineId} não encontrada`);
      return null;
    }

    if (!connection.isConnected || !connection.authenticated) {
      console.warn(`⚠️ Servidor OPC UA [${turbineId}] não conectado, usando dados simulados`);
      return this.generateSimulatedData(connection.server);
    }

    try {
      // Simular leitura de dados específicos do servidor
      const data = this.generateSimulatedData(connection.server);
      connection.lastData = data;
      
      console.log(`🔗 Dados OPC UA recebidos [${turbineId}] - Potência: ${data.activePower.toFixed(2)}MW, Vento: ${data.windSpeed.toFixed(1)}m/s, Status: ${this.getStatusText(data.operationalStatus)}`);
      
      return data;
    } catch (error) {
      console.error(`❌ Erro ao ler dados OPC UA [${turbineId}]:`, error);
      return connection.lastData;
    }
  }

  // Ler dados de todas as turbinas
  async readAllTurbineData(): Promise<Map<string, OPCUAData | null>> {
    const results = new Map<string, OPCUAData | null>();
    
    this.connections.forEach((connection, turbineId) => {
      const dataPromise = this.readTurbineData(turbineId);
      dataPromise.then(data => results.set(turbineId, data));
    });
    
    return results;
  }

  private generateSimulatedData(server: TurbineServer): OPCUAData {
    const now = new Date();
    
    // Usar ID da turbina para criar variações específicas
    const turbineHash = this.hashCode(server.id);
    const timeBase = Math.floor(now.getTime() / 5000); // Atualiza a cada 5s
    
    // Cada turbina tem padrões de vento ligeiramente diferentes
    let windSpeed = 8 + Math.sin((timeBase + turbineHash) * 0.1) * 7 + Math.random() * 2;
    windSpeed = Math.max(0, Math.min(25, windSpeed));
    
    // Calcular potência baseada na curva específica do modelo
    let activePower = this.calculatePowerCurve(windSpeed, server);
    
    // Adicionar variação realística específica da turbina
    const variationFactor = 0.95 + (Math.random() * 0.1);
    activePower *= variationFactor;
    activePower = Math.max(0, Math.min(server.maxPower, activePower));
    
    let rotorRPM = windSpeed > 3 ? 8 + (windSpeed - 3) * 0.8 : 0;
    let generatorTemp = 45 + (activePower / server.maxPower) * 35 + Math.sin((timeBase + turbineHash) * 0.05) * 5;
    const gearboxTemp = generatorTemp - 5 + Math.random() * 3;
    let vibration = 1.5 + (rotorRPM / 30) * 2 + Math.random() * 0.5;
    
    // Status operacional com probabilidades extremamente baixas e mais realistas
    let operationalStatus = 1;
    const statusRandom = Math.random() + (turbineHash % 100) / 100000; // Reduzido drasticamente
    if (statusRandom < 0.001) { // Reduzido para 0.1%
      if (statusRandom < 0.0002) operationalStatus = 2; // Atenção - 0.02%
      else if (statusRandom < 0.0005) operationalStatus = 3; // Manutenção - 0.03%
      else operationalStatus = 0; // Inativo - 0.05%
    }
    
    const bladeAngle = windSpeed > 15 ? 15 + (windSpeed - 15) * 2 : 0;
    const energyAccumulated = (now.getTime() / 1000 / 3600) * activePower; // MWh
    const availability = operationalStatus === 1 ? 95 + Math.random() * 5 : 
                        operationalStatus === 2 ? 80 + Math.random() * 15 : 0;

    return {
      activePower: Number(activePower.toFixed(2)),
      windSpeed: Number(windSpeed.toFixed(1)),
      rotorRPM: Number(rotorRPM.toFixed(1)),
      generatorTemp: Number(generatorTemp.toFixed(1)),
      gearboxTemp: Number(gearboxTemp.toFixed(1)),
      vibration: Number(vibration.toFixed(2)),
      operationalStatus,
      bladeAngle: Number(bladeAngle.toFixed(1)),
      energyAccumulated: Number(energyAccumulated.toFixed(2)),
      availability: Number(availability.toFixed(1)),
      lastUpdate: now
    };
  }

  private calculatePowerCurve(windSpeed: number, server: TurbineServer): number {
    const { maxPower } = server;
    
    // Curvas específicas por modelo
    if (server.model.includes('110/2.1')) {
      // WEG AGW 110/2.1MW
      if (windSpeed < 3 || windSpeed > 25) return 0;
      if (windSpeed <= 12) {
        return maxPower * Math.pow((windSpeed - 3) / 9, 3);
      } else {
        return maxPower;
      }
    } else if (server.model.includes('147/4.2')) {
      // WEG AGW 147/4.2MW
      if (windSpeed < 3.5 || windSpeed > 25) return 0;
      if (windSpeed <= 13) {
        return maxPower * Math.pow((windSpeed - 3.5) / 9.5, 3);
      } else {
        return maxPower;
      }
    }
    
    // Curva padrão
    if (windSpeed < 3 || windSpeed > 25) return 0;
    if (windSpeed <= 12) {
      return maxPower * Math.pow((windSpeed - 3) / 9, 3);
    } else {
      return maxPower;
    }
  }

  private hashCode(str: string): number {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash);
  }

  private getStatusText(status: number): string {
    switch (status) {
      case 1: return 'operational';
      case 2: return 'warning';
      case 3: return 'maintenance';
      case 0: return 'inactive';
      default: return 'unknown';
    }
  }

  // Obter informações dos servidores configurados
  getTurbineServers(): TurbineServer[] {
    return [...this.turbineServers];
  }

  // Obter status de conexão de uma turbina
  getConnectionStatus(turbineId: string): TurbineConnection | null {
    return this.connections.get(turbineId) || null;
  }

  // Obter status de todas as conexões
  getAllConnectionStatus(): Map<string, TurbineConnection> {
    return new Map(this.connections);
  }

  // Reconectar uma turbina específica
  async reconnectTurbine(turbineId: string): Promise<void> {
    const connection = this.connections.get(turbineId);
    if (connection) {
      connection.connectionAttempts = 0;
      await this.connectToServer(turbineId);
    }
  }

  // Testar autenticação de uma turbina segura
  async testAuthentication(turbineId: string, username: string, password: string): Promise<boolean> {
    const connection = this.connections.get(turbineId);
    if (!connection || !connection.server.requiresAuth) {
      return false;
    }

    console.log(`🔐 Testando autenticação para ${turbineId}: ${username}/***`);
    
    if (username === 'SCADA' && password === 'SCADA') {
      connection.authenticated = true;
      console.log(`✅ Teste de autenticação bem-sucedido para ${turbineId}`);
      return true;
    } else {
      console.log(`❌ Falha no teste de autenticação para ${turbineId}`);
      return false;
    }
  }

  // Testar conexão OPC UA antes de adicionar
  async testOpcuaConnection(config: Partial<TurbineServer>): Promise<{
    success: boolean;
    requiresAuth: boolean;
    message: string;
    detectedVariables?: string[];
  }> {
    try {
      const testUrl = `${config.url}:${config.port}${config.path || '/'}`;
      console.log(`🧪 Testando conexão OPC UA: ${testUrl}`);
      
      // Auto-detectar autenticação
      const requiresAuth = await this.detectAuthenticationRequirement(testUrl);
      
      if (requiresAuth && (!config.username || !config.password)) {
        return {
          success: false,
          requiresAuth: true,
          message: 'Servidor requer autenticação. Forneça usuário e senha.'
        };
      }
      
      // Simular teste de conexão e descoberta de variáveis
      const mockVariables = [
        'ActivePower', 'WindSpeed', 'RotorRPM', 
        'GeneratorTemp', 'GearboxTemp', 'Vibration',
        'OperationalStatus', 'BladeAngle', 'EnergyAccumulated'
      ];
      
      return {
        success: true,
        requiresAuth,
        message: requiresAuth ? 
          'Conexão bem-sucedida com autenticação' : 
          'Conexão bem-sucedida sem autenticação',
        detectedVariables: mockVariables
      };
      
    } catch (error) {
      return {
        success: false,
        requiresAuth: false,
        message: `Erro de conexão: ${error instanceof Error ? error.message : 'Erro desconhecido'}`
      };
    }
  }

  // Adicionar nova turbina com auto-configuração e teste
  async addTurbineConfiguration(config: {
    id: string;
    name: string;
    model: string;
    url: string;
    port: number;
    path?: string;
    username?: string;
    password?: string;
    maxPower: number;
    complex: string;
    park: string;
  }): Promise<{
    success: boolean;
    message: string;
    turbineId?: string;
  }> {
    try {
      // Verificar se ID já existe
      if (this.turbineServers.find(t => t.id === config.id)) {
        return {
          success: false,
          message: `Turbina com ID ${config.id} já existe`
        };
      }

      // Testar conexão primeiro
      const testResult = await this.testOpcuaConnection(config);
      if (!testResult.success) {
        return {
          success: false,
          message: testResult.message
        };
      }

      const newTurbine: TurbineServer = {
        ...config,
        // Garantir que o URL está completo
        url: config.url.startsWith('opc.tcp://') ? config.url : `opc.tcp://${config.url}`,
        path: config.path || '/PrimeWindTurbine',
        requiresAuth: testResult.requiresAuth,
        username: testResult.requiresAuth ? config.username : undefined,
        password: testResult.requiresAuth ? config.password : undefined
      };

      this.turbineServers.push(newTurbine);
      
      // Salvar no localStorage imediatamente
      this.saveTurbinesToStorage();
      
      // Criar conexão
      const connection: TurbineConnection = {
        server: newTurbine,
        isConnected: false,
        lastData: null,
        connectionAttempts: 0,
        authenticated: false
      };

      this.connections.set(newTurbine.id, connection);
      
      // Tentar conectar
      await this.connectToServer(newTurbine.id);
      
      console.log(`✅ Nova configuração de turbina adicionada: ${config.id}`);
      return {
        success: true,
        message: `Turbina ${config.id} configurada e conectada com sucesso (${this.turbineServers.length} turbinas ativas)`,
        turbineId: config.id
      };
    } catch (error) {
      console.error(`❌ Erro ao adicionar configuração de turbina:`, error);
      return {
        success: false,
        message: `Erro interno: ${error instanceof Error ? error.message : 'Erro desconhecido'}`
      };
    }
  }

  // Adicionar nova turbina manualmente (mantido para compatibilidade)
  async addTurbine(turbineConfig: TurbineServer): Promise<boolean> {
    try {
      // Verificar se ID já existe
      if (this.turbineServers.find(t => t.id === turbineConfig.id)) {
        console.error(`❌ Turbina com ID ${turbineConfig.id} já existe`);
        return false;
      }

      // Adicionar à lista de servidores
      this.turbineServers.push(turbineConfig);
      
      // Salvar no localStorage imediatamente
      this.saveTurbinesToStorage();
      
      // Criar conexão
      const connection: TurbineConnection = {
        server: turbineConfig,
        isConnected: false,
        lastData: null,
        connectionAttempts: 0,
        authenticated: false
      };

      this.connections.set(turbineConfig.id, connection);
      
      // Tentar conectar
      await this.connectToServer(turbineConfig.id);
      
      console.log(`✅ Turbina ${turbineConfig.id} adicionada com sucesso`);
      return true;
    } catch (error) {
      console.error(`❌ Erro ao adicionar turbina ${turbineConfig.id}:`, error);
      return false;
    }
  }

  // Remover turbina
  removeTurbine(turbineId: string): boolean {
    try {
      // Remover da lista de servidores
      const index = this.turbineServers.findIndex(t => t.id === turbineId);
      if (index === -1) {
        console.error(`❌ Turbina ${turbineId} não encontrada`);
        return false;
      }

      this.turbineServers.splice(index, 1);
      
      // Salvar no localStorage após remoção
      this.saveTurbinesToStorage();
      
      // Remover conexão
      this.connections.delete(turbineId);
      
      console.log(`✅ Turbina ${turbineId} removida com sucesso`);
      return true;
    } catch (error) {
      console.error(`❌ Erro ao remover turbina ${turbineId}:`, error);
      return false;
    }
  }

  // Testar conexão com configuração antes de adicionar
  async testConnection(config: Partial<TurbineServer>): Promise<boolean> {
    try {
      const testUrl = `${config.url}:${config.port}${config.path}`;
      console.log(`🔍 Testando conexão: ${testUrl}`);
      
      // Simular delay de teste
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Validações básicas obrigatórias
      if (!config.url || !config.port || !config.path) {
        console.log('❌ Configuração incompleta - URL, porta e caminho são obrigatórios');
        return false;
      }
      
      if (!config.id || !config.name) {
        console.log('❌ ID e nome do aerogerador são obrigatórios');
        return false;
      }
      
      if (!config.complex || !config.park) {
        console.log('❌ Complexo e parque eólico são obrigatórios para integração na hierarquia');
        return false;
      }
      
      if (config.port < 1024 || config.port > 65535) {
        console.log('❌ Porta inválida - deve estar entre 1024 e 65535');
        return false;
      }
      
      if (!config.url.includes('opc.tcp://')) {
        console.log('❌ URL inválida - deve começar com opc.tcp://');
        return false;
      }
      
      if (!config.path.startsWith('/')) {
        console.log('❌ Caminho inválido - deve começar com /');
        return false;
      }
      
      // Validação específica de autenticação
      if (config.requiresAuth) {
        if (!config.username || !config.password) {
          console.log('❌ Autenticação habilitada mas credenciais não fornecidas');
          return false;
        }
        
        // Simular teste de credenciais - aceitar apenas credenciais conhecidas
        const validCredentials = [
          { username: 'SCADA', password: 'SCADA' },
          { username: 'admin', password: 'admin123' },
          { username: 'opcua', password: 'opcua123' },
          { username: 'turbine', password: 'turbine123' }
        ];
        
        const credentialsValid = validCredentials.some(
          cred => cred.username === config.username && cred.password === config.password
        );
        
        if (!credentialsValid) {
          console.log(`❌ Credenciais inválidas - usuário '${config.username}' ou senha incorreta`);
          return false;
        }
        
        console.log(`✅ Credenciais válidas para usuário '${config.username}'`);
      }
      
      // Verificar se ID já existe
      const existingTurbine = this.turbineServers.find(t => t.id === config.id);
      if (existingTurbine) {
        console.log(`❌ ID '${config.id}' já está em uso por outro aerogerador`);
        return false;
      }
      
      // Verificar se porta já está em uso
      const portInUse = this.turbineServers.find(t => t.port === config.port);
      if (portInUse) {
        console.log(`❌ Porta ${config.port} já está em uso pelo aerogerador '${portInUse.id}'`);
        return false;
      }
      
      console.log(`✅ Teste de conexão bem-sucedido para ${testUrl}`);
      console.log(`✅ Todas as validações passaram para '${config.name}' (${config.id})`);
      return true;
      
    } catch (error) {
      console.error(`❌ Erro no teste de conexão:`, error);
      return false;
    }
  }

  // Obter lista de turbinas configuradas
  getConfiguredTurbines(): TurbineServer[] {
    return [...this.turbineServers];
  }

  // Obter lista de IDs das turbinas configuradas
  getTurbineIds(): string[] {
    return this.turbineServers.map(server => server.id);
  }

  // Forçar reset para configuração padrão com 3 turbinas
  public resetToDefaultConfiguration(): void {
    console.log('🔄 Forçando reset para configuração padrão com 3 turbinas...');
    localStorage.removeItem('turbineServers');
    this.connections.clear();
    this.setupDefaultTurbines();
    this.initializeConnections();
    console.log('✅ Configuração resetada com sucesso!');
  }

}

// Instância singleton do serviço
const multiOpcuaService = new MultiOPCUAService();
export { multiOpcuaService };
export default multiOpcuaService;